import { Timestamp } from 'firebase/firestore';
import { MaintenanceTask, MaintenanceFrequencyType, MaintenancePlan, MaintenanceAlertPriority } from '../types/maintenance';

// Types for creating new maintenance plans
type NewMaintenanceTask = Omit<MaintenanceTask, 'id' | 'planId'>;
type NewMaintenancePlan = Omit<Omit<MaintenancePlan, 'tasks'>, 'id' | 'createdAt' | 'updatedAt'> & {
  tasks: NewMaintenanceTask[];
};
import { FuelType } from '../types/vehicle';
import { addDays, addMonths, addYears, differenceInDays } from 'date-fns';

interface NextDueCalculation {
  nextMileageDue?: number;
  mileageMultiple?: number;
  nextTimeDue?: Date;
  timeMultiple?: number;
  effectiveDueDate: Date;
}

export const DEFAULT_MAINTENANCE_FREQUENCIES = {
  gasoline: {
    type: 'time_or_mileage' as MaintenanceFrequencyType,
    mileageValue: 15000,
    timeValue: 1,
    timeUnit: 'years' as const
  },
  diesel: {
    type: 'mileage' as MaintenanceFrequencyType,
    mileageValue: 20000
  },
  electric: {
    type: 'mileage' as MaintenanceFrequencyType,
    mileageValue: 30000
  },
  hybrid: {
    type: 'time_or_mileage' as MaintenanceFrequencyType,
    mileageValue: 15000,
    timeValue: 1,
    timeUnit: 'years' as const
  }
} as const;

export function getDefaultMaintenancePlan(
  vehicleId: string,
  organizationId: string,
  fuelType: FuelType,
  make: string,
  model: string,
  year: number,
  userId: string
): NewMaintenancePlan {
  const frequency = DEFAULT_MAINTENANCE_FREQUENCIES[fuelType];
  const now = Timestamp.now();

  // Construire la fréquence en fonction du type de carburant
  const taskFrequency: MaintenanceTask['frequency'] = {
    type: frequency.type,
    mileageValue: frequency.mileageValue,
    lastCompletedMileage: 0,
    lastCompletedDate: now,
    ...(('timeValue' in frequency && 'timeUnit' in frequency) ? {
      timeValue: frequency.timeValue,
      timeUnit: frequency.timeUnit
    } : {})
  };

  // Create task without ID since it will be assigned when saved to database
  const maintenanceTask: NewMaintenanceTask = {
    name: `Révision ${make} ${model} ${year}`,
    description: `Révision régulière basée sur le type de carburant (${fuelType})`,
    frequency: taskFrequency,
    priority: 'moderate',
    notificationSettings: {
      reminderFrequency: 7 // Rappel hebdomadaire par défaut
    },
    createdBy: userId,
    updatedBy: userId,
    createdAt: now,
    updatedAt: now
  };

  return {
    vehicleId,
    organizationId,
    name: `Plan de maintenance ${make} ${model} ${year}`,
    description: `Plan de maintenance généré automatiquement basé sur le type de carburant (${fuelType})`,
    isAutoGenerated: true,
    tasks: [maintenanceTask],
    createdBy: userId,
    updatedBy: userId
  };
}

export function convertToDays(value: number, unit: 'days' | 'months' | 'years'): number {
  switch (unit) {
    case 'days':
      return value;
    case 'months':
      return value * 30; // Approximation
    case 'years':
      return value * 365; // Approximation
    default:
      return value;
  }
}

export function addTimeUnit(date: Date, value: number, unit: 'days' | 'months' | 'years'): Date {
  switch (unit) {
    case 'days':
      return addDays(date, value);
    case 'months':
      return addMonths(date, value);
    case 'years':
      return addYears(date, value);
    default:
      return addDays(date, value);
  }
}

export function calculateNextDue(
  task: MaintenanceTask,
  currentMileage: number,
  averageDailyMileage: number
): NextDueCalculation {
  const { frequency } = task;
  const result: NextDueCalculation = {
    effectiveDueDate: new Date()
  };

  // Calcul basé sur le kilométrage
  if (frequency.mileageValue) {
    const lastMileage = frequency.lastCompletedMileage || 0;
    // Calculer combien de cycles complets depuis la dernière maintenance
    const cyclesCompleted = Math.floor((currentMileage - lastMileage) / frequency.mileageValue);
    result.mileageMultiple = cyclesCompleted + 1;
    result.nextMileageDue = lastMileage + (frequency.mileageValue * result.mileageMultiple);

    // Estimer la date d'atteinte du kilométrage
    const remainingMileage = result.nextMileageDue - currentMileage;
    const daysToMileageThreshold = Math.ceil(remainingMileage / averageDailyMileage);
    const estimatedMileageDate = addDays(new Date(), daysToMileageThreshold);
    
    if (frequency.type === 'mileage') {
      result.effectiveDueDate = estimatedMileageDate;
    }
  }

  // Calcul basé sur le temps
  if (frequency.timeValue && frequency.timeUnit) {
    const lastDate = frequency.lastCompletedDate?.toDate() || new Date(0);
    const timeSinceLastMaintenance = differenceInDays(new Date(), lastDate);
    const cycleDuration = convertToDays(frequency.timeValue, frequency.timeUnit);
    
    result.timeMultiple = Math.floor(timeSinceLastMaintenance / cycleDuration) + 1;
    result.nextTimeDue = addTimeUnit(
      lastDate,
      frequency.timeValue * result.timeMultiple,
      frequency.timeUnit
    );

    if (frequency.type === 'time') {
      result.effectiveDueDate = result.nextTimeDue;
    }
  }

  // Pour le type 'time_or_mileage', prendre la date la plus proche
  if (frequency.type === 'time_or_mileage' && result.nextTimeDue) {
    const mileageDate = result.nextMileageDue 
      ? addDays(new Date(), Math.ceil((result.nextMileageDue - currentMileage) / averageDailyMileage))
      : new Date(8640000000000000); // Date max

    result.effectiveDueDate = new Date(Math.min(
      result.nextTimeDue.getTime(),
      mileageDate.getTime()
    ));
  }

  return result;
}

export function estimateDateForMileage(
  currentMileage: number,
  targetMileage: number,
  averageDailyMileage: number
): Date {
  const remainingMileage = targetMileage - currentMileage;
  const daysToTarget = Math.ceil(remainingMileage / averageDailyMileage);
  return addDays(new Date(), daysToTarget);
}

export function calculatePriority(dueDate: Date): 'critical' | 'moderate' | 'low' {
  const daysUntilDue = differenceInDays(dueDate, new Date());
  
  if (daysUntilDue <= 7) return 'critical';
  if (daysUntilDue <= 30) return 'moderate';
  return 'low';
}
